import { useEffect, useId, useState, type ChangeEvent } from 'react';
import { Eye } from 'lucide-react';
import { DisabledHint } from '@/components/common/DisabledHint';
import { PhotoPreviewDialog } from '@/components/common/PhotoPreviewDialog';
import { Button, buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { FIELD_CONTROL_CLASS } from '@/styles/fieldControl';
import { FIELD_LABEL_CLASS } from './formDialog.constants';

const FILE_PICK_CONTROL_CLASS = cn(
  'flex w-full cursor-pointer items-center gap-2.5 border border-input px-1.5 text-sm focus-within:border-primary focus-within:ring-4 focus-within:ring-primary/12',
  FIELD_CONTROL_CLASS,
);

const NO_FILE_TEXT = 'No file chosen';
const NO_FILE_PREVIEW_HINT = 'Choose a picture to preview it';
const NOT_A_PICTURE_PREVIEW_HINT = 'Only a picture can be previewed';
const IMAGE_TYPE_PREFIX = 'image/';

function getPreviewHint(pickedFile: File | null): string | null {
  if (pickedFile === null) return NO_FILE_PREVIEW_HINT;
  if (!pickedFile.type.startsWith(IMAGE_TYPE_PREFIX)) return NOT_A_PICTURE_PREVIEW_HINT;
  return null;
}

interface FormFilePickFieldProps {
  label: string;
  accept: string;
}

export function FormFilePickField({ label, accept }: FormFilePickFieldProps) {
  const inputId = useId();
  const [pickedFile, setPickedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const previewHint = getPreviewHint(pickedFile);

  useEffect(() => {
    if (previewUrl === null) return undefined;
    return () => URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  function handlePicked(event: ChangeEvent<HTMLInputElement>) {
    setPickedFile(event.target.files?.[0] ?? null);
  }

  function handlePreview() {
    if (pickedFile === null) return;
    setPreviewUrl(URL.createObjectURL(pickedFile));
  }

  const previewButton = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={handlePreview}
      disabled={previewHint !== null}
    >
      <Eye aria-hidden />
      Preview
    </Button>
  );

  return (
    <div data-slot="form-item" className="flex min-w-0 flex-col gap-2">
      <span data-slot="form-label" className={FIELD_LABEL_CLASS}>
        {label}
      </span>
      <label htmlFor={inputId} className={FILE_PICK_CONTROL_CLASS}>
        <span className={buttonVariants({ variant: 'outline', size: 'sm' })}>Choose File</span>
        <span
          className={cn(
            'min-w-0 wrap-break-word',
            pickedFile === null ? 'text-placeholder' : 'font-medium',
          )}
        >
          {pickedFile?.name ?? NO_FILE_TEXT}
        </span>
        <input
          id={inputId}
          type="file"
          accept={accept}
          aria-label={label}
          className="sr-only"
          onChange={handlePicked}
        />
      </label>
      <div>
        {previewHint === null ? (
          previewButton
        ) : (
          <DisabledHint hint={previewHint} ariaLabel={`Preview ${label}`}>
            {previewButton}
          </DisabledHint>
        )}
      </div>
      {previewUrl === null ? null : (
        <PhotoPreviewDialog
          open
          onOpenChange={(isOpen) => {
            if (!isOpen) setPreviewUrl(null);
          }}
          photoUrl={previewUrl}
          title={pickedFile?.name ?? label}
        />
      )}
    </div>
  );
}
